import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';
import { preventBadNumberInputKeys, preventBadNumberInputValues } from '../../../input-management';
import { Tooltip } from '../../Tooltip';
import { Typography } from '../../Typography';
import { tableListClasses } from '../TableList.classes';
import styles from './InlineControl.module.scss';
export const InlineInput = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { value, error, errorText, errorTextInTooltip, inputProps, testId, className, onChange, onFocus, onBlur, onSave } = props;
    const inputRef = useRef(null);
    // workaround to reuse the same ref
    useImperativeHandle(ref, ()=>{
        var _inputRef_current;
        return (_inputRef_current = inputRef.current) !== null && _inputRef_current !== void 0 ? _inputRef_current : new HTMLInputElement();
    });
    // The initial value when focusing on the input.
    const [pristineValue, setPristineValue] = useState(value);
    // Logic for blur on {enter} and undo on {escape}.
    useEffect(()=>{
        const inputEl = inputRef.current;
        const onKeyDown = (event)=>{
            if (!inputEl || document.activeElement !== inputEl) {
                return;
            }
            if (event.key === 'Enter') {
                inputEl.blur();
            } else if (event.key === 'Escape') {
                onChange === null || onChange === void 0 ? void 0 : onChange(pristineValue);
                inputEl.blur();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return ()=>{
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [
        onChange,
        pristineValue
    ]);
    // Save mechanism
    const [valueToSave, setValueToSave] = useState(null);
    // should be called after validation process has completed
    useEffect(()=>{
        if (valueToSave !== null) {
            setValueToSave(null);
            if (!error && valueToSave !== pristineValue) {
                setPristineValue(valueToSave);
                onSave === null || onSave === void 0 ? void 0 : onSave(valueToSave);
            }
        }
    }, [
        error,
        onSave,
        pristineValue,
        valueToSave
    ]);
    const isNumberInput = inputProps && inputProps.type === 'number';
    return /*#__PURE__*/ _jsx(Tooltip, {
        title: errorTextInTooltip && errorText ? /*#__PURE__*/ _jsx("div", {
            "data-testid": testId && "".concat(testId, "-error"),
            children: errorText
        }) : '',
        interactive: true,
        disableArrow: true,
        disableHoverListener: true,
        disableTouchListener: true,
        children: /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.root, className),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.frame, styles.absolutePosition, tableListClasses.showOnRowHover)
                }),
                /*#__PURE__*/ _jsx(Typography, _object_spread_props(_object_spread({}, inputProps), {
                    ellipsis: true,
                    as: "input",
                    ref: inputRef,
                    weight: "normal",
                    className: cn(styles.formElement, styles.absolutePosition, {
                        [styles.error]: error
                    }),
                    value: value,
                    testId: testId,
                    "data-test-error": error || undefined,
                    tooltipProps: {
                        disableFocusListener: true
                    },
                    onChange: (e)=>{
                        const value = e.currentTarget.value;
                        const inFocus = document.activeElement === e.currentTarget;
                        isNumberInput && preventBadNumberInputValues(e);
                        onChange === null || onChange === void 0 ? void 0 : onChange(value);
                        if (!inFocus) {
                            setValueToSave(value.trim());
                        }
                    },
                    onKeyDown: isNumberInput ? (e)=>preventBadNumberInputKeys(e, inputProps.min != null && +inputProps.min >= 0) : undefined,
                    onFocus: (e)=>{
                        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
                    },
                    onBlur: (e)=>{
                        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
                        setValueToSave(e.currentTarget.value.trim());
                    }
                })),
                errorText && !errorTextInTooltip && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph3",
                    skin: "error",
                    className: styles.errorText,
                    testId: testId && "".concat(testId, "-error"),
                    children: errorText
                })
            ]
        })
    });
});
